import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, CheckCircle2, Loader2, Trophy, Clock } from "lucide-react";
import { handsChallenges, handsChallengeJoin, handsChallengeComplete } from "@/lib/handsApi";

export default function HandsChallenges() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { setData(await handsChallenges()); }
    catch { toast.error("Couldn't load challenges"); }
  };
  useEffect(() => { load(); }, []);

  const act = async (slug, kind) => {
    setBusyId(slug);
    try {
      if (kind === "join") { await handsChallengeJoin(slug); toast.success("Joined — you got this!"); }
      else { const r = await handsChallengeComplete(slug); toast.success(r.reward_granted ? "Completed! Reward credited" : "Completed"); }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  if (!data) return <div className="py-16 text-center text-white/40">Loading challenges…</div>;

  return (
    <div data-testid="hands-challenges" className="pb-6 space-y-4">
      <section className="rounded-3xl bg-gradient-to-br from-[#F59E0B]/25 via-[#0F2118] to-[#0A1512] border border-[#F59E0B]/30 p-5">
        <div className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"><Flame className="w-3 h-3 text-[#F59E0B]" /> Savings challenges</div>
        <div className="font-black text-2xl">Build the habit. Get rewarded.</div>
        <div className="text-white/60 text-sm mt-1">Every completed challenge credits your Jixxeo balance.</div>
      </section>

      <Group title="Active" testId="ch-active" items={data.active} tone="green" busyId={busyId} onAct={act} showComplete />
      <Group title="Available" testId="ch-available" items={data.available} tone="dim" busyId={busyId} onAct={act} showJoin />
      <Group title="Completed" testId="ch-completed" items={data.completed} tone="gold" busyId={busyId} showBadge />
    </div>
  );
}

function Group({ title, items, tone, testId, busyId, onAct, showJoin, showComplete, showBadge }) {
  if (!items || items.length === 0) return null;
  const color = tone === "green" ? "text-[#22C55E]" : tone === "gold" ? "text-[#F59E0B]" : "text-white/50";
  return (
    <section data-testid={testId}>
      <h2 className={`font-black text-sm uppercase tracking-widest mb-2 ${color}`}>{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.slug} data-testid={`ch-${c.slug}`} className="bg-[#0F2118] rounded-2xl border border-[#1D3A2E] p-4">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#22C55E]">{c.category}</div>
                <div className="font-black">{c.title}</div>
                <div className="text-xs text-white/60 mt-1">{c.desc}</div>
              </div>
              {showBadge && <Trophy className="w-6 h-6 text-[#F59E0B]" />}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-white/50">
              <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {c.days}d</div>
              <div className="inline-flex items-center gap-1">Reward on complete</div>
            </div>
            {showJoin && (
              <button data-testid={`ch-join-${c.slug}`} onClick={() => onAct?.(c.slug, "join")} disabled={busyId === c.slug}
                className="mt-3 w-full bg-[#22C55E] text-[#0A1512] font-black text-sm py-2.5 rounded-full disabled:opacity-50">
                {busyId === c.slug ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Join challenge"}
              </button>
            )}
            {showComplete && (
              <button data-testid={`ch-complete-${c.slug}`} onClick={() => onAct?.(c.slug, "complete")} disabled={busyId === c.slug}
                className="mt-3 w-full bg-gradient-to-r from-[#22C55E] to-[#84CC16] text-[#0A1512] font-black text-sm py-2.5 rounded-full disabled:opacity-50 inline-flex items-center justify-center gap-1">
                {busyId === c.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Mark complete</>}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
